import { Box, Typography, Button, styled, alpha } from "@mui/material";
import {
  Inbox as InboxIcon,
  People as PeopleIcon,
  EventNote as EventNoteIcon,
  Assignment as AssignmentIcon,
  AttachMoney as AttachMoneyIcon,
  Settings as SettingsIcon,
  Search as SearchIcon,
} from "@mui/icons-material";

interface EnhancedEmptyScreenProps {
  type:
  | "patients"
  | "clinicians"
  | "sessions"
  | "reports"
  | "payouts"
  | "pricing"
  | "general"
  | "search";
  message?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
}

const EmptyContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(10, 4),
  textAlign: "center",
  minHeight: 420,
  animation: "fadeIn 0.4s ease-out",
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: 100,
  height: 100,
  borderRadius: 24,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: alpha(theme.palette.primary.main, 0.06),
  marginBottom: theme.spacing(3),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  "& svg": {
    fontSize: 44,
    color: theme.palette.primary.main,
    opacity: 0.7,
  },
}));

const getEmptyStateConfig = (type: string) => {
  const configs: Record<
    string,
    { icon: any; title: string; subtitle: string }
  > = {
    patients: {
      icon: PeopleIcon,
      title: "No Patients Found",
      subtitle:
        "There are no patients in the system yet. Patients will appear here once they register.",
    },
    clinicians: {
      icon: PeopleIcon,
      title: "No Clinicians Found",
      subtitle:
        "There are no clinicians in the system yet. Add clinicians to get started.",
    },
    sessions: {
      icon: EventNoteIcon,
      title: "No Sessions Found",
      subtitle:
        "There are no sessions scheduled. Sessions will appear here once they are created.",
    },
    reports: {
      icon: AssignmentIcon,
      title: "No Reports Found",
      subtitle:
        "There are no reports available. Reports will appear here once clinicians submit them.",
    },
    payouts: {
      icon: AttachMoneyIcon,
      title: "No Payouts Found",
      subtitle:
        "There are no payouts to display. Payouts will appear here once they are processed.",
    },
    pricing: {
      icon: SettingsIcon,
      title: "No Pricing Configurations",
      subtitle:
        "There are no pricing configurations set up. Add pricing to get started.",
    },
    search: {
      icon: SearchIcon,
      title: "No Results Found",
      subtitle:
        "We couldn't find any results matching your search. Try different keywords.",
    },
    general: {
      icon: InboxIcon,
      title: "No Data Available",
      subtitle: "There is no data to display at the moment.",
    },
  };

  return configs[type] || configs.general;
};

export const EnhancedEmptyScreen = ({
  type,
  message,
  action,
  secondaryAction,
}: EnhancedEmptyScreenProps) => {
  const config = getEmptyStateConfig(type);
  const Icon = config.icon;

  return (
    <EmptyContainer>
      <IconWrapper>
        <Icon />
      </IconWrapper>

      <Typography variant="h4" fontWeight={600} gutterBottom>
        {config.title}
      </Typography>

      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ maxWidth: 500, mb: 4 }}
      >
        {message || config.subtitle}
      </Typography>

      <Box display="flex" gap={2}>
        {action && (
          <Button variant="contained" size="large" onClick={action.onClick}>
            {action.label}
          </Button>
        )}
        {secondaryAction && (
          <Button
            variant="outlined"
            size="large"
            onClick={secondaryAction.onClick}
          >
            {secondaryAction.label}
          </Button>
        )}
      </Box>
    </EmptyContainer>
  );
};

export default EnhancedEmptyScreen;

export { };
